import React, { useEffect, useRef, useState } from 'react'

import PropTypes from 'prop-types'

import { Root, TableFilterInputWrapper, TableFilterWideInputWrapper } from '../../styles/components/TableFiltersStyle'
import scanApi from '../../api/scan'
import useHandleError from '../../api/useHandleError'

import {
  AutoCompleteFilterInput,
  DateTimePickerFilterInput,
  SearchFieldFilterInput,
  SelectFilterInput,
} from './inputs'

const scanFiltersConfig = [
  {
    field: 'search',
    headerName: 'Search (Fazit)',
    type: 'searchField',
    size: 'wide',
  },
  {
    field: 'scanned_at_from',
    headerName: 'Scanned At From',
    type: 'datetime',
    size: 'medium',
  },
  {
    field: 'scanned_at_to',
    headerName: 'Scanned At To',
    type: 'datetime',
    size: 'medium',
  },
  {
    field: 'computer__in',
    headerName: 'Computer',
    type: 'autocomplete',
    size: 'medium',
  },
  {
    field: 'user__username__in',
    headerName: 'Username',
    type: 'autocomplete',
    size: 'medium',
  },
  {
    field: 'has_report',
    headerName: 'Has Report',
    type: 'select',
    options: {
      Yes: true,
      No: false,
    },
    size: 'medium',
  },
  {
    field: 'filter_final_ECU_number__in',
    headerName: 'Filter Final ECU Number',
    type: 'autocomplete',
    size: 'medium',
  },
  {
    field: 'filter_final_sw__in',
    headerName: 'Filter Final SW',
    type: 'autocomplete',
    size: 'medium',
  }
]

const ScanFilters = ({ setFilters, usedFilters }) => {
  const [selectedValues, setSelectedValues] = useState(usedFilters)
  const [renderedFilters, setRenderedFilters] = useState([])
  const [filterOptions, setFilterOptions] = useState(null)
  const isInitialMount = useRef(true)
  const handleError = useHandleError()

  const handleSelectedValues = (e, selectedObject, reason) => {
    let newSelectedValue = {}

    if (selectedObject) {
      newSelectedValue = selectedObject
    } else {
      const { name, value } = e.target
      newSelectedValue = { [name]: value }
    }

    const selectedFilterValue = Object.values(newSelectedValue)[0]
    const filterNameToBeCleared = Object.keys(newSelectedValue)[0]

    const nextSelectedFilters = {
      ...selectedValues,
      ...newSelectedValue,
    }

    if (reason === 'clear' || typeof selectedFilterValue === 'undefined' || selectedFilterValue === '') {
      const clearedFilters = { ...selectedValues }
      delete clearedFilters[filterNameToBeCleared]
      return setSelectedValues(clearedFilters)
    }

    setSelectedValues(nextSelectedFilters)
  }

  const getFilterOptions = () =>
    scanApi
      .filters()
      .then((data) => {
        const scanFilters = data.data || {}
        return {
          computer__in: scanFilters.computers || [],
          user__username__in: scanFilters.usernames || [],
          filter_final_ECU_number__in: scanFilters.filter_final_ECU_numbers || [],
          filter_final_sw__in: scanFilters.filter_final_sws || [],
        }
      })
      .catch(handleError)

  const getRenderedFilters = (options) => {
    return scanFiltersConfig.map((filter, index) => {
      const FilterWrapper = filter.size === 'wide' ? TableFilterWideInputWrapper : TableFilterInputWrapper
      let Field = null

      switch (filter.type) {
        case 'datetime':
          Field = (
            <DateTimePickerFilterInput
              key={index}
              filter={filter}
              options={options}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              isSetToMidnight={filter.field === 'scanned_at_to'}
              sx={{ width: '100%' }}
            />
          )
          break
        case 'autocomplete':
          Field = (
            <AutoCompleteFilterInput
              key={index}
              filter={filter}
              options={options}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              aria-label={`${filter.field}-autocomplete-filter`}
              sx={{ width: '100%' }}
            />
          )
          break
        case 'select':
          Field = (
            <SelectFilterInput
              key={index}
              filter={filter}
              options={options}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              sx={{ width: '100%' }}
            />
          )
          break
        case 'searchField':
          Field = (
            <SearchFieldFilterInput
              key={index}
              filter={filter}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              sx={{ width: '100%' }}
            />
          )
          break
      }

      return <FilterWrapper key={index}>{Field}</FilterWrapper>
    })
  }

  useEffect(() => {
    const updateFilters = async () => {
      const options = filterOptions ? filterOptions : await getFilterOptions()

      if (isInitialMount.current) {
        setFilterOptions(options)
      }

      isInitialMount.current = false
      setRenderedFilters(getRenderedFilters(options || {}))
      setFilters(selectedValues)
    }

    updateFilters()
  }, [selectedValues])

  useEffect(() => {
    const isClearedSelectedValues = Object.keys(usedFilters).length === 0

    if (isClearedSelectedValues && usedFilters !== selectedValues && !isInitialMount.current) {
      setSelectedValues({})
    }
  }, [usedFilters])

  return <Root className="sticky-vertical viewportWidth">{renderedFilters}</Root>
}

ScanFilters.propTypes = {
  setFilters: PropTypes.func.isRequired,
  usedFilters: PropTypes.object,
}

export default ScanFilters
